import { startCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import { metricsDetails } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';

export const getKeyCount = (object): number => Object.keys(object).length;

export const useDataSource = (object, isMetric = false) => {
  const { t } = useTranslation();
  const { tvalue, tunit } = useMeasurement();

  const dataSource = Array.isArray(object)
    ? object.map(({ metric, value }) => {
        const unit = metricsDetails[metric]?.unit;
        const fractionDigits = metricsDetails[metric]?.fractionDigits ?? 2;
        return {
          key: t(metricsDetails[metric]?.text ?? startCase(metric)),
          value: isMetric && !isNaN(Number(value)) ? tvalue(Number(value).toFixed(fractionDigits), unit) : value,
          unit: tunit(unit),
        };
      })
    : Object.entries(object).map(([key, value]) => ({
        key,
        value,
      }));

  return dataSource.filter(({ value }) => !Array.isArray(value));
};

export const getValue = value => {
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value.toString();
  return value;
};

export const useDeviceAdditionalDetails = (entity: Entity) => {
  const { devicesStore, stationStore } = useStores();
  const deviceAdditionalDetails = devicesStore.getDeviceById(entity?.deviceId);

  let modelAttributes = deviceAdditionalDetails?.deviceModel?.attributes;
  let modelSpecs = deviceAdditionalDetails?.deviceModel?.modelSpecs;
  let metrics = entity?.additionalDetails ?? [];
  let deviceAttributes = deviceAdditionalDetails?.attributes;

  const onDevices = stationStore.currentStation?.devices?.filter(device => entity.onDevices.includes(device.deviceId));
  onDevices?.forEach(onDevice => {
    const onDeviceAdditionalDetails = devicesStore.getDeviceById(onDevice?.deviceId);
    const onDeviceModelAttributes = onDeviceAdditionalDetails?.deviceModel?.attributes;
    const onDeviceModelSpecs = onDeviceAdditionalDetails?.deviceModel?.modelSpecs;
    const onDeviceMetrics = onDevice?.additionalDetails ?? [];
    const onDeviceAttributes = onDeviceAdditionalDetails?.attributes;

    modelAttributes = { ...onDeviceModelAttributes, ...modelAttributes };
    modelSpecs = { ...onDeviceModelSpecs, ...modelSpecs };
    metrics = [...onDeviceMetrics, ...metrics];
    deviceAttributes = { ...onDeviceAttributes, ...deviceAttributes };
  });

  const sectionsMap = {
    metrics: metrics,
    modelAttributes: modelAttributes,
    modelSpecs: modelSpecs,
    deviceAttributes: deviceAttributes,
  };

  return sectionsMap;
};
